
import { Component, toNative, Vue, Watch } from 'vue-facing-decorator';
import NewParticipantGrid from './NewParticipantGrid.vue';
import Loading from '@/components/Common/loading.vue';
import SideDrawer from '@/components/Common/SideDrawer.vue';
import Avatar from 'vue3-avatar';
import { FetchData, Root, Store } from '@/main';
import CommonStyle from '@/helper/CommonStyle';
import { StoreHelper } from '@/store/StoreHelper';
import { AppColorSettingsInfo } from '@/entities/Application/AppColorSettingsInfo';
import ParticipantExtraInformation from '@/entities/Participants/ParticipantExtraInformation';
import ParticipantGroupDto from '@/entities/Participants/ParticipantGroupDto';
import TicketDetailsViewModel from '@/entities/Participants/TicketDetailsViewModel';
import StripeTicketInstanceDto from '@/entities/Participants/StripeTicketInstanceDto';
import IdentityCard from '../Common/IdentityCard.vue';

import { EventAttendeeDto } from '@/entities/Participants/EventAttendeeDto';
import ParticipantPayLoadViewModel from '@/entities/Participants/ParticipantPayLoadViewModel';
import readXlsxFile from 'read-excel-file';
import ParticipantsImportValidation from '@/entities/Participants/ParticipantsImportValidation';

@Component({
  components: { NewParticipantGrid, Loading, SideDrawer, Avatar, IdentityCard },
})
class ParentParticipantGrid extends Vue {
  // ── Icons ──────────────────────────────────────
  private readonly filterIcon         = require('@/assets/Images/blue-filter-icon.svg');
  private readonly filterSelectedIcon = require('@/assets/Images/filter-selected-icon.svg');
  private readonly importIcon         = require('@/assets/Images/import-icon.svg');
  private readonly exportIcon         = require('@/assets/Images/export-icon.svg');
  private readonly emailIcon          = require('@/assets/Images/EventSettingFeaturesAndIntegrationImages/ActiveIcons/ventla-invite-icon-active.svg');
  private readonly custmiseColumn     = require('@/assets/Images/customise-column-icon.svg');
  private readonly crossIcon          = require('@/assets/Images/blue-circular-cross-icon.svg');
  private readonly alertCrossIcon = require('@/assets/Images/alert-cross-icon.svg');
  private readonly alertTraingleIcon = require('@/assets/Images/alert-triangle-icon.svg');
  private readonly infoIcon = require('@/assets/Images/info-i-alert-icon.svg');
  private readonly maxImportLimit = 5000;
  // Store event attendes
  private eventAttendees: EventAttendeeDto[] = [];

  // ── Avatar color helpers (reads app theme colours from the store) ─────────
  private readonly storeHelper = new StoreHelper(Store);

  private GetDefaultBackgroundColor(): string {
    const colors: AppColorSettingsInfo = this.storeHelper.GetApplicationColorSettingsFromStore();
    return colors.topNavBackgroundColor;
  }

  private GetDefaultForegroundColor(): string {
    const colors: AppColorSettingsInfo = this.storeHelper.GetApplicationColorSettingsFromStore();
    return colors.topNavForegroundColor;
  }

  // ── Feature flags ─────────────────────────────────────────────────────────
  private get enableRegistrationSystem(): boolean {
    return this.storeHelper.GetRequestInfoFromStore().EnableRegistrationSystem;
  }

  private get showTickets(): boolean {
    const req = this.storeHelper.GetRequestInfoFromStore();
    return req.RequiredEventTicket || req.OfferTickets;
  }

  // ── Participant data ──────────────────────────────────────────────────────
  private participants: EventAttendeeDto[] = [];
  /** Count of items matching the current search/filter query — used for pagination. */
  private totalCount = 0;
  /** Unfiltered total of all participants in the event. */
  private totalParticipants = 0;
  private totalInvitesSent = 0;
  private totalCompletedRegistrations = 0;
  private totalJoinedParticipants = 0;
  private totalSelfCheckedIn = 0;

  /**
   * Full list of groups for this event — sourced from EventAttendeeListDto.AllGroupsList.
   * Used to resolve GroupIds on each attendee to human-readable group names.
   */
  private allGroups: ParticipantGroupDto[] = [];

  /**
   * Master list of all My Information fields for this event — sourced from
   * EventAttendeeListDto.AllExtraInformations.
   * Used in the edit drawer to render every field (including Manual ones a
   * participant may not have a value for yet), with Source determining editability.
   */
  private allExtraInformations: ParticipantExtraInformation[] = [];

  // ── Pagination state (controlled by this component, passed down as props) ─
  private skip     = 0;
  private pageSize = 100;

  // ── Loading / export flags ────────────────────────────────────────────────
  private isLoading   = false;
  private isExporting = false;
  private readonly loaderBorderColor = CommonStyle.SelectedItemHeadingColor;

  // ── Search ────────────────────────────────────────────────────────────────
  private searchTerm = '';
  private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  // ── Filters ───────────────────────────────────────────────────────────────
  private showFilterPanel = false;

  // Applied state — used by BuildPayload
  private selectedNameFilter    = 'all';
  private selectedCheckInFilter = 'all';
  private selectedStatuses:      string[] = [];
  private selectedEmailStatuses: string[] = [];
  private selectedTicketTypes:   string[] = [];
  private selectedGroupIds:      number[] = [];
  private selectedHasNote        = false;

  // Draft state — edited inside the drawer, committed on Apply
  private draftNameFilter    = 'all';
  private draftCheckInFilter = 'all';
  private draftStatuses:      string[] = [];
  private draftEmailStatuses: string[] = [];
  private draftTicketTypes:   string[] = [];
  private draftGroupIds:      number[] = [];
  private draftHasNote        = false;

  private readonly availableStatuses      = ['Not Invited', 'Invited', 'Attending', 'Not attending'];
  private readonly availableEmailStatuses = ['Not sent', 'Sent', 'Delivered', 'Opened', 'Clicked', 'Sending', 'Bounced'];
  private availableTicketTypes: TicketDetailsViewModel[] = [];

  // ── Sort ──────────────────────────────────────────────────────────────────
  private sortBy = 'FirstName';
  private sortDir = 'ASC';

  // ── API 1 cache ───────────────────────────────────────────────────────────
  private cachedEventTickets: TicketDetailsViewModel[] = [];
  private cachedStripeTicketInstances: StripeTicketInstanceDto[] = [];
  // ── Import drawer state ──────────────────────────────────────────────
  private showImportDrawer = false;
  private importFile: File | null = null;
  private importExcelFile: File | null = null;
  private importTooltipVisible = false;
  private showImportedParticipantsTable = false;
  private showImportSuccessfullScreen = false;
  private importedParticipantsTablesScreenWidth = '1420px';
  private readonly defaultParticipantsExcelColumns: string[] = ['Email', 'FirstName', 'LastName', 'Company', 'Job Title', 'Phone', 'About', 'XProfileUrl', 'InstagramUrl', 'LinkedinUrl', 'FacebookUrl', 'WebsiteURL', 'Admin notes'];
  private matchedExtraInformationHeaders: string[] = [];
  private ignoredExtraInformationHeaders: string[] = [];
  private maximumExcelRowsAllowed = 5000;
  private extraColumnsWarningMessage = '';
  private totalParticipantsImportedMessage = '';
  private showExtraColumnsWarning = false;
  private showExcelValidationsErrors = false;
  private showTotalParticipantsImportedInfo = false;
  private totalExcelRowsCount = 0;

  private excelRows: any[][] = [];
  private excelHeaders: string[] = [];
  private successIcon = require('@/assets/Images/circular-tick-icon.svg');

  // ── Edit participant drawer state ───────────────────────────────────
  private showEditDrawer = false;
  private activeEditTab = 'profile';
  private editTabs = [
    { id: 'profile', label: 'Profile' },
    { id: 'registration', label: 'Registration' },
    { id: 'groups', label: 'Groups' },
    { id: 'myInformation', label: 'My Information' },
    { id: 'tickets', label: 'Tickets & Check-ins' },
    { id: 'notes', label: 'Notes' },
  ];
  private editForm: EventAttendeeDto = EventAttendeeDto.createEmpty();

  // ── Notes tab derived fields (not part of EventAttendeeDto) ─────────────
  /** Editable note text — pre-filled from the first note in ParticipantNotes. */
  private editNoteText = '';
  /**
   * Read-only metadata shown below the textarea when a prior note exists.
   * editNoteIsUpdated = true  → labels read "Updated by / Updated on"
   * editNoteIsUpdated = false → labels read "Created by / Created on"
   */
  private editNoteUpdatedBy = '';
  private editNoteUpdatedOn = '';
  private editNoteIsUpdated = false;

  private showImportExcelError = false;
  private showImportExcelMaxRowsError = false;
  private validationErrorMesssage = '';
  private importValidationErrors: ParticipantsImportValidation[] = [];
  // ── Grid selection ────────────────────────────────────────────────────────
  // Updated by NewParticipantGrid via the 'selection-change' emit
  private selectedCount = 0;
  private isAllPagesSelected = false;
  private get activeFilterCount(): number {
    return (
      (this.selectedNameFilter !== 'all' ? 1 : 0) +
      (this.selectedCheckInFilter !== 'all' ? 1 : 0) +
      this.selectedStatuses.length +
      this.selectedEmailStatuses.length +
      this.selectedTicketTypes.length +
      this.selectedGroupIds.length +
      (this.selectedHasNote ? 1 : 0)
    );
  }

  // ── Column chooser ────────────────────────────────────────────────────────
  private showColumnChooser = false;
  // Applied state — passed to the grid; keys match EventAttendeeDto field names
  private columnVisibility: Record<string, boolean> = {
    name: true, Title: true, Company: true,
    InviteEmailStatus: true, RegistrationStatus: true, RegisteredOn: true,
    ParticipantTicketDetails: true,
    CheckInDateTime: true, JoinedByIOS: true, groups: true,
  };
  // Draft state — edited inside the drawer, committed on Save
  private draftColumnVisibility: Record<string, boolean> = {};

  private get effectiveColumnVisibility(): Record<string, boolean> {
    return {
      ...this.columnVisibility,
      InviteEmailStatus:        this.enableRegistrationSystem && (this.columnVisibility.InviteEmailStatus  !== false),
      RegistrationStatus:       this.enableRegistrationSystem && (this.columnVisibility.RegistrationStatus !== false),
      RegisteredOn:             this.enableRegistrationSystem && (this.columnVisibility.RegisteredOn       !== false),
      ParticipantTicketDetails: this.showTickets              && (this.columnVisibility.ParticipantTicketDetails !== false),
    };
  }

  private get allColumnGroups(): Array<{ title: string; fields: Array<{ field: string; title: string }> }> {
    const extraInfoFields = this.allExtraInformations.map((info) => ({
      field: info.KeyValue,
      title: info.KeyValue,
    }));
    return [
      { title: 'PROFILE',         fields: [{ field: 'name', title: 'Name' }, { field: 'Title', title: 'Title' }, { field: 'Company', title: 'Company' }] },
      ...(this.enableRegistrationSystem ? [{ title: 'REGISTRATION',   fields: [{ field: 'InviteEmailStatus', title: 'Invite Email' }, { field: 'RegistrationStatus', title: 'Status' }, { field: 'RegisteredOn', title: 'Registered on' }] }] : []),
      ...(this.showTickets              ? [{ title: 'TICKET DETAILS',  fields: [{ field: 'ParticipantTicketDetails', title: 'Tickets' }] }] : []),
      { title: 'SELF CHECKED-IN', fields: [{ field: 'CheckInDateTime', title: 'Checked-in on' }] },
      { title: 'APP DETAILS',     fields: [{ field: 'JoinedByIOS', title: 'Platform' }] },
      { title: 'GROUPS DETAILS',  fields: [{ field: 'groups', title: 'Groups' }] },
      ...(extraInfoFields.length  ? [{ title: 'EXTRA INFORMATION', fields: extraInfoFields }] : []),
    ];
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────
  private mounted() {
    Root.on('edit-participant', (val: any) => {
      if (val?.dataItem) {
        this.OpenEditDrawer(val.dataItem as EventAttendeeDto);
      }
    });
    this.FetchInitialLoad();
  }

  private beforeUnmount() {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
  }

  // ── Data fetching ─────────────────────────────────────────────────────────

  private get selectedStripeInstanceIds(): number[] | null {
    if (!this.selectedTicketTypes.length) return null;
    const ticketIds = this.selectedTicketTypes
      .map((name) => this.cachedEventTickets.find((t) => t.Name === name)?.Id)
      .filter((id): id is number => id !== undefined);
    if (!ticketIds.length) return null;
    const instanceIds = this.cachedStripeTicketInstances
      .filter((inst) => ticketIds.includes(inst.StripeProductId))
      .map((inst) => inst.Id);
    return instanceIds.length > 0 ? instanceIds : null;
  }

  private BuildPayload(): ParticipantPayLoadViewModel {
    return new ParticipantPayLoadViewModel(
      this.skip,
      this.pageSize,
      this.searchTerm.trim(),
      this.selectedNameFilter,
      this.selectedCheckInFilter,
      this.selectedStatuses,
      this.selectedEmailStatuses,
      this.selectedTicketTypes,
      this.selectedGroupIds,
      this.selectedHasNote,
      this.sortBy,
      this.sortDir,
      this.selectedStripeInstanceIds,
    );
  }

  private FetchInitialLoad() {
    this.isLoading = true;
    FetchData('/EventAttendee/GetAttendeeList', JSON.stringify(this.BuildPayload()))
      .then((response: any) => {
        this.allGroups                   = response.AllGroupsList               ?? [];
        this.allExtraInformations        = response.AllExtraInformations        ?? [];
        this.cachedEventTickets          = (response.EventTickets               ?? []) as TicketDetailsViewModel[];
        this.cachedStripeTicketInstances = (response.StripeTicketInstances      ?? []) as StripeTicketInstanceDto[];
        this.availableTicketTypes        = this.cachedEventTickets;
        this.assignTicketOptions         = this.cachedEventTickets;
        this.totalCount                  = response.FilteredCount               ?? 0;
        this.totalParticipants           = response.TotalParticipants           ?? 0;
        this.totalInvitesSent            = response.TotalInvitesSent            ?? 0;
        this.totalCompletedRegistrations = response.TotalCompletedRegistrations ?? 0;
        this.totalJoinedParticipants     = response.TotalJoinedParticipants     ?? 0;
        this.totalSelfCheckedIn          = response.TotalSelfCheckedIn          ?? 0;
        this.participants                = response.Attendees                   ?? [];
        Root.emit('participant-stats-loaded', {
          totalInvitesSent:            this.totalInvitesSent,
          totalCompletedRegistrations: this.totalCompletedRegistrations,
          totalJoinedParticipants:     this.totalJoinedParticipants,
          totalSelfCheckedIn:          this.totalSelfCheckedIn,
          displayParticipantsInApp:    response.DisplayParticipantsInApp   ?? false,
          hideParticipantsEmailInApp:  response.HideParticipantsEmailInApp ?? false,
          allGroups:                   this.allGroups,
        });
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  private FetchPage() {
    this.isLoading = true;
    FetchData('/EventAttendee/GetAttendeePage', JSON.stringify(this.BuildPayload()))
      .then((response: any) => {
        this.totalCount   = response.FilteredCount ?? 0;
        this.participants = this.EnrichTicketNames(response.Attendees ?? []);
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  private EnrichTicketNames(attendees: EventAttendeeDto[]) {
    return attendees.map((attendee) => {
      attendee.ParticipantTicketDetails = attendee.ParticipantTicketDetails.map((ticket) => {
        if (ticket.TicketName) return ticket;

        if (ticket.StripeTicketInstanceId != null) {
          // Stripe / paid ticket: resolve via StripeTicketInstance → EventTicket
          // StripeTicketInstanceId arrives as a string from the API despite the DTO typing it as number
          const instanceId = Number(ticket.StripeTicketInstanceId);
          const instance = this.cachedStripeTicketInstances.find(
            (i) => i.Id === instanceId,
          );
          if (instance) {
            const eventTicket = this.cachedEventTickets.find(
              (t) => t.Id === instance.StripeProductId,
            );
            if (eventTicket) ticket.TicketName = eventTicket.Name;
          }
        } else {
          // Admin-assigned / free ticket: Id maps directly to EventTicket.Id
          const eventTicket = this.cachedEventTickets.find((t) => t.Id === ticket.Id);
          if (eventTicket) ticket.TicketName = eventTicket.Name;
        }

        return ticket;
      });
      return attendee;
    });
  }

  // ── Event handlers ────────────────────────────────────────────────────────

  private OnSortChange(payload: { field: string; dir: string }) {
    const fieldMap: Record<string, string> = {
      email:   'Email',
      name:    'FirstName',
      Title:   'Title',
      Company: 'Company',
    };
    const backendField = fieldMap[payload.field];
    if (!backendField) return;

    this.sortBy  = backendField;
    this.sortDir = payload.dir;
    this.skip    = 0;
    this.FetchPage();
  }

  private OnPageChange(page: { skip: number; take: number }) {
    this.skip     = page.skip >= this.totalCount ? 0 : page.skip;
    this.pageSize = page.take;
    this.FetchPage();
  }

  private ClearSearch() {
    this.searchTerm = '';
    this.skip       = 0;
    this.FetchPage();
  }

  // Copies applied filters into draft state and opens the drawer
  private OpenFilterDrawer() {
    this.draftNameFilter    = this.selectedNameFilter;
    this.draftCheckInFilter = this.selectedCheckInFilter;
    this.draftStatuses      = [...this.selectedStatuses];
    this.draftEmailStatuses = [...this.selectedEmailStatuses];
    this.draftTicketTypes   = [...this.selectedTicketTypes];
    this.draftGroupIds      = [...this.selectedGroupIds];
    this.draftHasNote       = this.selectedHasNote;
    this.showFilterPanel    = true;
  }

  private ToggleStatusFilter(status: string) {
    const idx = this.draftStatuses.indexOf(status);
    if (idx === -1) {
      this.draftStatuses.push(status);
    } else {
      this.draftStatuses.splice(idx, 1);
    }
  }

  private ToggleEmailStatusFilter(emailStatus: string) {
    const idx = this.draftEmailStatuses.indexOf(emailStatus);
    if (idx === -1) {
      this.draftEmailStatuses.push(emailStatus);
    } else {
      this.draftEmailStatuses.splice(idx, 1);
    }
  }

  private ToggleTicketTypeFilter(ticketType: string) {
    const idx = this.draftTicketTypes.indexOf(ticketType);
    if (idx === -1) {
      this.draftTicketTypes.push(ticketType);
    } else {
      this.draftTicketTypes.splice(idx, 1);
    }
  }

  private ToggleGroupFilter(groupId: number) {
    const idx = this.draftGroupIds.indexOf(groupId);
    if (idx === -1) {
      this.draftGroupIds.push(groupId);
    } else {
      this.draftGroupIds.splice(idx, 1);
    }
  }

  // Commits draft filters → applied state, re-fetches data, closes drawer
  private ApplyFilters() {
    this.selectedNameFilter    = this.draftNameFilter;
    this.selectedCheckInFilter = this.draftCheckInFilter;
    this.selectedStatuses      = [...this.draftStatuses];
    this.selectedEmailStatuses = [...this.draftEmailStatuses];
    this.selectedTicketTypes   = [...this.draftTicketTypes];
    this.selectedGroupIds      = [...this.draftGroupIds];
    this.selectedHasNote       = this.draftHasNote;
    this.skip = 0;
    this.FetchPage();
    this.showFilterPanel = false;
  }

  // Clears applied filters from toolbar "Clear filters" action and refreshes list
  private ClearAppliedFilters() {
    this.selectedNameFilter    = 'all';
    this.selectedCheckInFilter = 'all';
    this.selectedStatuses      = [];
    this.selectedEmailStatuses = [];
    this.selectedTicketTypes   = [];
    this.selectedGroupIds      = [];
    this.selectedHasNote       = false;
    this.draftNameFilter       = 'all';
    this.draftCheckInFilter    = 'all';
    this.draftStatuses         = [];
    this.draftEmailStatuses    = [];
    this.draftTicketTypes      = [];
    this.draftGroupIds         = [];
    this.draftHasNote          = false;
    this.skip = 0;
    this.FetchInitialLoad();
  }

  // Clears all draft selections inside the open drawer (does not close it)
  private ClearDraftFilters() {
    this.draftNameFilter    = 'all';
    this.draftCheckInFilter = 'all';
    this.draftStatuses      = [];
    this.draftEmailStatuses = [];
    this.draftTicketTypes   = [];
    this.draftGroupIds      = [];
    this.draftHasNote       = false;
  }

  // Copies applied column visibility into draft state and opens the drawer
  private OpenColumnDrawer() {
    this.draftColumnVisibility = { ...this.columnVisibility };
    this.showColumnChooser = true;
  }

  // Toggles a column checkbox in the draft (not yet applied)
  private ToggleDraftColumn(field: string) {
    this.draftColumnVisibility = {
      ...this.draftColumnVisibility,
      [field]: !(this.draftColumnVisibility[field] !== false),
    };
  }

  // Commits draft columns → applied state, closes drawer
  private SaveColumns() {
    this.columnVisibility = { ...this.draftColumnVisibility };
    this.showColumnChooser = false;
  }

  private OnSelectionChange(count: number) {
    this.selectedCount = count;
    if (count === 0) {
      this.isAllPagesSelected = false;
      if (this.exportScope === 'selected') {
        this.exportScope = 'all';
      }
    }
  }

  private OnSelectAll() {
    this.isAllPagesSelected = true;
    this.selectedCount = this.totalCount;
  }

  private OnClearSelectAll() {
    this.isAllPagesSelected = false;
  }

  private OnBulkAction(payload: { action: string; selectedItems: any[] }) {
    if (payload.action === 'assign-ticket') {
      this.selectedAssignTicketIds = [];
      this.showAssignTicketDrawer = true;
      return;
    }
    if (payload.action === 'revoke-ticket') {
      this.selectedRevokeTicketIds = [];
      this.showRevokeTicketDrawer = true;
      return;
    }
    if (payload.action === 'export') {
      // Pre-select "Selected attendees" scope since the user has an active selection
      this.exportScope = 'selected';
      this.showExportDrawer = true;
      return;
    }
    if (payload.action === 'send-email') {
      // Open the send-email drawer in bulk/selection context
      this.isBulkEmailContext = true;
      this.selectedEmailType = 'invitations';
      this.showSendEmailDrawer = true;
      return;
    }
    if (payload.action === 'assign-group') {
      this.OpenAssignGroupDrawer();
      return;
    }
    // TODO: wire up remaining bulk actions (assign-ticket, revoke-ticket, assign-group, clear-checkins, delete)
  }

  // Reset export scope to "all" when all filters are cleared while "filtered" was chosen
  @Watch('activeFilterCount')
  private OnActiveFilterCountChange(val: number) {
    if (val === 0 && this.exportScope === 'filtered') {
      this.exportScope = 'all';
    }
  }

  // ── Export drawer state ─────────────────────────────────────────────
  private showExportDrawer = false;
  private exportScope = 'all';

  /** Count shown in the Export button — reflects whichever scope is chosen */
  private get ExportCount(): number {
    if (this.exportScope === 'selected') {
      return this.selectedCount;
    }
    if (this.exportScope === 'filtered') {
      return this.totalCount;
    }
    return this.totalParticipants;
  }
  private exportCategories = [
    {
      // Profile is mandatory — always exported; disabled so it cannot be unchecked
      id: 'profile', label: 'Profile', checked: true, disabled: true,
      children: [
        // Profile details is a mandatory sub-set of Profile; always included
        { id: 'profileDetails', label: 'Profile details', checked: true, disabled: true },
        // Social is optional — user may uncheck it
        { id: 'social', label: 'Social', checked: true, disabled: false },
      ],
    },
    { id: 'registration',          label: 'Registration',          checked: true,  disabled: false, children: [] },
    { id: 'registrationResponses', label: 'Registration responses', checked: false, disabled: false, children: [] },
    { id: 'ticketDetails',         label: 'Ticket details',         checked: false, disabled: false, children: [] },
    { id: 'appDetails',            label: 'App details',            checked: false, disabled: false, children: [] },
    { id: 'groupDetails',          label: 'Group details',          checked: false, disabled: false, children: [] },
    { id: 'myInformation',         label: 'My Information',         checked: false, disabled: false, children: [] },
  ];

  // ── Send email drawer state ─────────────────────────────────────────
  private showSendEmailDrawer = false;
  private selectedEmailType = 'invitations';
  private showAssignTicketDrawer = false;
  private showRevokeTicketDrawer = false;
  private showAssignGroupDrawer = false;
  private assignGroupMode: 'existing' | 'new' = 'existing';
  private selectedAssignGroupId: number | null = null;
  private newAssignGroupName = '';
  private assignTicketOptions: TicketDetailsViewModel[] = [];
  private selectedAssignTicketIds: number[] = [];
  private selectedRevokeTicketIds: number[] = [];
  /**
   * true  → opened from the selection bar (bulk context)
   * false → opened from the toolbar "Send emails" button (all/filtered context)
   * The two contexts differ only in the 4th option:
   *   toolbar:       "Send Custom Email"
   *   selection bar: "Resend ticket details to selected"
   */
  private isBulkEmailContext = false;

  private get emailTypes(): Array<{ id: string; label: string; description: string; lastSent: string }> {
    const shared = [
      { id: 'invitations',  label: 'Send Invitations',      description: 'Send the event invitation email to participants who haven\'t received one yet.', lastSent: 'January 02, 2026 11:09 AM' },
      { id: 'reminder',     label: 'Send Reminder',         description: 'Send a reminder to the participants who haven\'t responded to their invitation yet.', lastSent: 'January 02, 2026 11:09 AM' },
      { id: 'resendTicket', label: 'Resend ticket details', description: 'Resend ticket details to the ticket holders.', lastSent: 'January 02, 2026 11:09 AM' },
    ];
    if (this.isBulkEmailContext) {
      return [
        ...shared,
        { id: 'resendTicketSelected', label: 'Resend ticket details to selected', description: 'Resend ticket details to the selected ticket holders.', lastSent: 'January 02, 2026 11:09 AM' },
      ];
    }
    return [
      ...shared,
      { id: 'customEmail', label: 'Send Custom Email', description: 'Send a custom email to selected participants.', lastSent: 'January 02, 2026 11:09 AM' },
    ];
  }

  private get paidAssignTickets(): TicketDetailsViewModel[] {
    return this.assignTicketOptions.filter((ticket) => Number(ticket.Price ?? 0) > 0);
  }

  private get freeAssignTickets(): TicketDetailsViewModel[] {
    return this.assignTicketOptions.filter((ticket) => Number(ticket.Price ?? 0) <= 0);
  }

  // ── Import drawer methods ──────────────────────────────────────────
  private TriggerImportFileInput() {
    (this.$refs.importFileInput as HTMLInputElement).click();
  }

  private OpenImportDrawer() {
    this.importFile = null;
    this.showImportDrawer = true;
  }

  // Get total participants count to show in table
  private GetTotalParticipantsCountText(count: number, text: string){
    return this.$t(text, [count]).toString();
  }

  private OnImportFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }
    this.showExcelValidationsErrors = false;
    this.importValidationErrors = [];
    const file = input.files[0];
    const fileName = file.name.toLowerCase();

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      this.showImportExcelError = true;
      this.validationErrorMesssage = this.$t('Participant.Import.InvalidFileSizeError').toString();
      input.value = '';
      return;
    }

    //  Validate extension
    const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');

    if (!isExcel) {
      this.showImportExcelError = true;
      input.value = '';
      return;
    }

    this.showImportExcelError = false;

    // Read file using read-excel-file
    readXlsxFile(file)
      .then((rows: any[][]) => {
        if (!rows || rows.length === 0) {
          this.showImportExcelError = false;
          return;
        }
        const headers = (rows[0]).map((header: any) => String(header ?? '').trim());
        const dataRows = rows.slice(1);
        // Validate mandatory columns with exact name + exact casing
        const hasAllRequiredColumns = this.defaultParticipantsExcelColumns.every(
          (requiredColumn) => headers.includes(requiredColumn),
        );

        if (!hasAllRequiredColumns) {
          this.showImportExcelError = true;
          this.validationErrorMesssage = this.$t('Participant.Import.InvalidImportFile').toString();
          this.excelHeaders = [];
          this.excelRows = [];
          return;
        }
        this.CheckMatchingExtraInformationHeaders(headers);
        const filteredColumnIndexes = headers
          .map((header: string, index: number) => {
            const isDefaultColumn = this.defaultParticipantsExcelColumns.includes(header);
            const isMatchedExtraColumn = this.matchedExtraInformationHeaders.includes(header);

            return isDefaultColumn || isMatchedExtraColumn ? index : -1;
          })
          .filter((index: number) => index !== -1);
        this.excelHeaders = filteredColumnIndexes.map((index: number) => headers[index]);
        this.excelRows = dataRows.map((row: any[]) =>
          filteredColumnIndexes.map((index: number) => row[index]),
        );
        // this.ValidateImportedEmailRows(this.excelHeaders, this.excelRows);
        // this.totalExcelRowsCount = this.excelRows.length;
        // this.showTotalParticipantsImportedInfo = true;
        // this.totalParticipantsImportedMessage = this.$t('Participant.Import.TotalParticipantsImported', [this.totalExcelRowsCount]).toString();
        // this.showImportExcelError = false;
        // this.importFile = file;
        // this.CheckExcelMaximumRowsAllowed(dataRows, file);
        const isValid = this.ValidateImportedEmailRows(this.excelHeaders, this.excelRows);
        if (!isValid) {
          this.showExcelValidationsErrors = true;
          this.showTotalParticipantsImportedInfo = false;
          this.showExcelValidationsErrors = true;
          this.importFile = file;
          // return;
        }

        // this.showExcelValidationsErrors = false;
        this.totalExcelRowsCount = this.excelRows.length;
        this.showTotalParticipantsImportedInfo = true;
        this.totalParticipantsImportedMessage = this.$t('Participant.Import.TotalParticipantsImported', [this.totalExcelRowsCount]).toString();
        this.showImportExcelError = false;
        this.importFile = file;
        this.CheckExcelMaximumRowsAllowed(dataRows, file);

      })
      .catch(() => {
        this.showImportExcelError = true;
      });
    input.value = '';
  }

  // Check for matching information form my information
  private CheckMatchingExtraInformationHeaders(headers: string[]) {
    const extraInformationHeaderSet = new Set(
      this.allExtraInformations
        .map((item: any) => item.KeyValue)
        .filter((keyValue: string) => !!keyValue),
    );

    const nonDefaultHeaders = headers.filter(
      (header) => !this.defaultParticipantsExcelColumns.includes(header),
    );

    this.matchedExtraInformationHeaders = nonDefaultHeaders.filter(
      (header) => extraInformationHeaderSet.has(header),
    );

    this.ignoredExtraInformationHeaders = nonDefaultHeaders.filter(
      (header) => !extraInformationHeaderSet.has(header),
    );

    if (this.ignoredExtraInformationHeaders.length > 0) {
      const columnNames = this.ignoredExtraInformationHeaders.join(', ');

      this.extraColumnsWarningMessage = this.$t(
        'Participant.Import.ExtraColumnsWarningMessage',
        [columnNames],
      ).toString();
      this.showExtraColumnsWarning = true;
    }

  }

  // Check for excel rows
  private CheckExcelMaximumRowsAllowed(rowsData: any, file: any){
    if(rowsData.length > this.maximumExcelRowsAllowed){
      this.importExcelFile = null;
      this.showImportExcelMaxRowsError = true;
      this.validationErrorMesssage = this.$t('Participant.Import.MaximumRowsImportFileError', [rowsData.length, this.maximumExcelRowsAllowed]).toString();
    }else{
      this.importExcelFile = file;
    }
  }

  private ValidateImportedEmailRows(headers: string[], rows: any[][]): boolean {
    const errors: ParticipantsImportValidation[] = [];

    const emailColumnIndex = headers.indexOf('Email');

    if (emailColumnIndex === -1) {
      return true; // safety
    }

    const emailSet = new Map<string, number[]>();

    rows.forEach((row: any[], rowIndex: number) => {
      const rowNo = rowIndex + 2;
      const email = String(row[emailColumnIndex] ?? '').trim();

      if (!email) {
        errors.push(new ParticipantsImportValidation(rowNo, '', 'Email address is missing'));
        return;
      }

      const invalidChars = /[(),:;<>[\]\s]/.test(email);
      if (invalidChars) {
        errors.push(new ParticipantsImportValidation(rowNo, email, 'Email address has invalid character(s)'));
        return;
      }

      const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!regex.test(email)) {
        errors.push(new ParticipantsImportValidation(rowNo, email, 'Email address is invalid'));
        return;
      }

      const normalized = email.toLowerCase();
      if (!emailSet.has(normalized)) {
        emailSet.set(normalized, []);
      }
      emailSet.get(normalized)?.push(rowNo);
    });

    // duplicates
    emailSet.forEach((rows, email) => {
      if (rows.length > 1) {
        rows.forEach((rowNo) => {
          errors.push(new ParticipantsImportValidation(rowNo, email, 'Duplicate email address'));
        });
      }
    });

    this.importValidationErrors = errors;

    return errors.length === 0;
  }

  private ConfirmImport() {
    this.showImportDrawer = false;
    this.showImportedParticipantsTable = true;
    // TODO: upload importFile via FormData + FetchData
  }

  // Function to close the Drawer
  private CloseImportDrawer(){
    this.showImportDrawer = false;
    this.showImportExcelError = false;
  }

  // ── Export drawer methods ──────────────────────────────────────────
  private OpenExportDrawer() {
    // Toolbar button always starts with "All attendees" selected
    this.exportScope = 'all';
    this.showExportDrawer = true;
  }

  private ConfirmExport() {
    this.showExportDrawer = false;
    // TODO: FetchData('/Participant/ExportParticipants', JSON.stringify({ scope: this.exportScope, categories: this.exportCategories }), ApiContentType.Excel)
  }

  private ConfirmImportParticipantsToExcel(){
    this.FillExcelImport();
    this.ExportParticipantsData();
  }

  // Close ImportParticipantsToExcel drawer
  private CloseImportParticipantsToExcel(){
    this.showImportedParticipantsTable = false;
    this.showImportSuccessfullScreen = false;
  }

  private ExportParticipantsData(){
    this.showImportSuccessfullScreen = true;
    FetchData('','').then(()=>{

    }).catch(()=>{
      // log error if any
    });
  }

  private FillExcelImport(){
    const headers = this.excelHeaders.map((h) => h.toLowerCase().trim());
    this.eventAttendees = [];
    this.excelRows.forEach((row) => {
      const attendee = EventAttendeeDto.createEmpty();

      attendee.FirstName = row[headers.indexOf('first name')] || '';
      attendee.LastName  = row[headers.indexOf('last name')] || '';
      attendee.Email     = row[headers.indexOf('email')] || '';
      attendee.Phone     = row[headers.indexOf('phone')] || '';
      attendee.Company   = row[headers.indexOf('company')] || '';
      attendee.Title     = row[headers.indexOf('title')] || '';
      attendee.PhotoUrl  = row[headers.indexOf('photo url')] || '';
      attendee.IconUrl   = row[headers.indexOf('icon url')] || '';
      attendee.About     = row[headers.indexOf('about')] || '';
      // Social
      attendee.TwitterHandle               = row[headers.indexOf('twitter')] || '';
      attendee.FacebookProfileId           = row[headers.indexOf('facebook')] || '';
      attendee.LinkedInPublicProfileUrl    = row[headers.indexOf('linkedin')] || '';
      attendee.Instagram                  = row[headers.indexOf('instagram')] || '';
      attendee.Website                    = row[headers.indexOf('website')] || '';

      this.eventAttendees.push(attendee);
    });
  }


  // ── Send email drawer methods ──────────────────────────────────────
  private OpenSendEmailDrawer() {
    this.isBulkEmailContext = false;
    this.selectedEmailType = 'invitations';
    this.showSendEmailDrawer = true;
  }

  private ConfirmSendEmail() {
    this.showSendEmailDrawer = false;
    // TODO: FetchData('/Participant/SendEmails', JSON.stringify({ type: this.selectedEmailType }))
  }

  private ToggleAssignTicket(ticketId: number) {
    const idx = this.selectedAssignTicketIds.indexOf(ticketId);
    if (idx === -1) {
      this.selectedAssignTicketIds.push(ticketId);
    } else {
      this.selectedAssignTicketIds.splice(idx, 1);
    }
  }

  private GetTicketAvailabilityText(ticket: TicketDetailsViewModel): string {
    if (!ticket.LimitQuantity) {
      return 'Unlimited';
    }
    return `${Math.max(ticket.AvailableQuantityIncludingReserve, 0)}/${ticket.Quantity}`;
  }

  private FormatTicketPrice(ticket: TicketDetailsViewModel): string {
    const price = Number(ticket.Price ?? 0);
    if (price <= 0) {
      return 'Free';
    }
    const amount = Number.isInteger(price) ? String(price) : price.toFixed(2);
    return `${ticket.CurrencySymbol || ticket.Currency || ''} ${amount}`.trim();
  }

  private ConfirmAssignTickets() {
    this.showAssignTicketDrawer = false;
    // TODO: wire API call to assign selectedAssignTicketIds to selected participants
  }

  private ToggleRevokeTicket(ticketId: number) {
    const idx = this.selectedRevokeTicketIds.indexOf(ticketId);
    if (idx === -1) {
      this.selectedRevokeTicketIds.push(ticketId);
    } else {
      this.selectedRevokeTicketIds.splice(idx, 1);
    }
  }

  private ConfirmRevokeTickets() {
    this.showRevokeTicketDrawer = false;
    // TODO: wire API call to revoke selectedRevokeTicketIds from selected participants
  }

  private get isAssignGroupConfirmDisabled(): boolean {
    if (this.assignGroupMode === 'existing') {
      return this.selectedAssignGroupId === null;
    }
    return this.newAssignGroupName.trim().length === 0;
  }

  private OpenAssignGroupDrawer() {
    this.assignGroupMode = 'existing';
    this.selectedAssignGroupId = null;
    this.newAssignGroupName = '';
    this.showAssignGroupDrawer = true;
  }

  private CloseAssignGroupDrawer() {
    this.showAssignGroupDrawer = false;
  }

  private ConfirmAssignGroup() {
    this.showAssignGroupDrawer = false;
    // TODO: wire API call to assign selected participants to a group
    // existing group: this.selectedAssignGroupId
    // new group: this.newAssignGroupName
  }

  // ── Edit participant drawer methods ────────────────────────────────
  private OpenEditDrawer(attendee: EventAttendeeDto) {
    // Prefer the original DTO from the fetched list (avoids issues with spread objects
    // that the Kendo grid adds computed fields — selected, groups — to).
    const source = this.participants.find((p) => p.Id === attendee.Id) ?? attendee;

    // Shallow-copy into a fresh instance so edits don't mutate the grid row.
    const dto = EventAttendeeDto.createEmpty();
    Object.assign(dto, source);

    // Arrays that are mutated in the drawer need independent copies.
    dto.GroupIds                 = [...source.GroupIds];
    dto.ParticipantTicketDetails = [...source.ParticipantTicketDetails];

    // My Information: merge EventAttendeeListDto.AllExtraInformations with this row so
    // every event field (including manually created) appears; empty values stay editable.
    // Match by KeyValue; fall back to KeyInformation when KeyValue is empty (some APIs).
    const sourceExtras = source.ExtraInformations ?? [];
    if (this.allExtraInformations?.length) {
      dto.ExtraInformations = this.allExtraInformations.map((field) => {
        const existing = sourceExtras.find(
          (e) =>
            (field.KeyValue && e.KeyValue === field.KeyValue)
            || (!field.KeyValue && field.KeyInformation && e.KeyInformation === field.KeyInformation),
        );
        const keyLabel = field.KeyValue || field.KeyInformation || existing?.KeyValue || '';
        return new ParticipantExtraInformation(
          existing?.Id ?? field.Id,
          existing?.ParticipantId || source.Id,
          existing?.ApplicationInstanceId ?? field.ApplicationInstanceId,
          keyLabel,
          existing?.KeyInformation ?? '',
          field.EventId ?? existing?.EventId ?? 0,
          field.SortOrder ?? existing?.SortOrder ?? 0,
          existing?.Source ?? field.Source,
          field.IsVisibleInApp ?? existing?.IsVisibleInApp ?? false,
          field.ParticipantsCount ?? existing?.ParticipantsCount ?? 0,
          existing?.IsDeleted ?? field.IsDeleted ?? false,
        );
      });
    } else {
      dto.ExtraInformations = sourceExtras.map(
        (e) => new ParticipantExtraInformation(
          e.Id,
          e.ParticipantId,
          e.ApplicationInstanceId,
          e.KeyValue,
          e.KeyInformation,
          e.EventId,
          e.SortOrder,
          e.Source,
          e.IsVisibleInApp,
          e.ParticipantsCount,
          e.IsDeleted,
        ),
      );
    }

    this.editForm = dto;

    // Notes tab — flatten to a single editable note.
    // For the meta row: prefer UpdatedBy/UpdatedOn when set, fall back to CreatedBy/CreatedOn.
    const firstNote = source?.ParticipantNotes?.[0];
    this.editNoteText      = firstNote?.Note ?? '';
    this.editNoteIsUpdated = !!(firstNote?.UpdatedBy || firstNote?.UpdatedOn);
    this.editNoteUpdatedBy = firstNote ? (firstNote.UpdatedBy || firstNote.CreatedBy) : '';
    this.editNoteUpdatedOn = firstNote ? String(firstNote.UpdatedOn || firstNote.CreatedOn) : '';

    this.activeEditTab = 'profile';
    this.showEditDrawer = true;
  }

  /** Toggle a group in the participant's GroupIds list when a checkbox is changed. */
  private ToggleGroupMembership(groupId: number) {
    const idx = this.editForm.GroupIds.indexOf(groupId);
    if (idx === -1) {
      this.editForm.GroupIds.push(groupId);
    } else {
      this.editForm.GroupIds.splice(idx, 1);
    }
  }

  private FormatDateTime(val?: string | null): string {
    if (!val) {
      return '-';
    }
    const d = new Date(val);
    return isNaN(d.getTime()) ? '-' : d.toLocaleString();
  }

  private FormatTicketDate(val?: Date | string | null): string {
    if (!val) {
      return '-';
    }
    const d = val instanceof Date ? val : new Date(String(val));
    return isNaN(d.getTime()) ? '-' : d.toLocaleString();
  }

  private RevokeTicket(idx: number) {
    this.editForm.ParticipantTicketDetails.splice(idx, 1);
  }

  private ConfirmEditParticipant() {
    this.showEditDrawer = false;
    console.log('Edited participant data to be sent to API:', JSON.stringify(this.editForm));
    // TODO: FetchData('/Participant/Update', JSON.stringify(this.editForm))
  }

  private OnRowAction(payload: { action: string; dataItem: any }) {
    if (payload.action === 'edit') {
      this.OpenEditDrawer(payload.dataItem as EventAttendeeDto);
    }
  }
  private OnSearchTermChange() {
      if (this.searchTerm.trim() !== '' || this.searchTerm === '') {
        this.skip = 0;
        this.FetchPage();
      }
  }
  // @Watch('searchTerm')
  // private OnSearchTermChange(val: string) {
  //   if (this.searchDebounceTimer) {
  //     clearTimeout(this.searchDebounceTimer);
  //   }
  //   this.searchDebounceTimer = setTimeout(() => {
  //     // Fire on non-empty input, or when cleared back to empty to restore full list
  //     if (val.trim() !== '' || val === '') {
  //       this.skip = 0;
  //       this.FetchPage();
  //     }
  //   }, 400);
  // }
}

export default toNative(ParentParticipantGrid);
